/* BP-024 · Market Intelligence Page — /v3/market */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plus, Search, X, Map, TrendingUp,
  Sparkles, Building2, ChevronRight } from "lucide-react";

const TIER = {
  hot:       { text: "#065f46", bg: "#a7f3d0", border: "#059669" },
  stable:    { text: "#047857", bg: "#d1fae5", border: "#10b981" },
  softening: { text: "#b45309", bg: "#fef3c7", border: "#f59e0b" },
  declining: { text: "#b91c1c", bg: "#fee2e2", border: "#dc2626" },
};

function fmt$(n) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(v)) return "—";
  return `$${v.toLocaleString()}`;
}


export default function MarketIntelligencePage() {
  const [zips, setZips] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, z] = await Promise.all([
        api.get("/v3/market/dashboard"),
        api.get("/v3/market/zips", { params: q ? { q } : {} }),
      ]);
      setDash(d.data);
      setZips(z.data || []);
    } catch { toast.error("Failed to load market data"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/v3/market/refresh");
      toast.success(`Refreshed ${data.refreshed} markets · ${data.alerts_created} new alerts`);
      load();
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24"
          data-testid="market-intelligence-page">
      <div className="max-w-[1400px] mx-auto px-4 pt-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-sky-600">
              Market Intelligence · BP-024
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Market Intelligence</h1>
            <div className="text-sm text-slate-500">
              ZIP-level market health · trend detection · strategy fit
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} disabled={refreshing}
                    data-testid="market-refresh-btn"
                    className="text-sm px-3 py-2 rounded-md
                                bg-gradient-to-r from-sky-500 to-cyan-500
                                text-white hover:from-sky-600 hover:to-cyan-600
                                flex items-center gap-1.5 disabled:opacity-50">
              {refreshing
                ? <Loader2 size={14} className="animate-spin"/>
                : <RefreshCw size={14}/>}
              Refresh All
            </button>
            <button onClick={() => setShowNew(true)}
                    data-testid="market-new-zip-btn"
                    className="text-sm px-3 py-2 rounded-md
                                bg-slate-900 text-white hover:bg-slate-800
                                flex items-center gap-1.5">
              <Plus size={14}/> Track ZIP
            </button>
          </div>
        </div>

        {dash && (
          <div className="grid gap-3 md:grid-cols-5 mb-4"
                data-testid="market-kpi-row">
            {[
              { label: "Markets Tracked", v: dash.market_count, testid: "kpi-count" },
              { label: "Avg Health",       v: dash.avg_score ?? "—", testid: "kpi-avg" },
              { label: "Hot",              v: dash.tier_counts?.hot ?? 0, testid: "kpi-hot" },
              { label: "Declining",        v: dash.tier_counts?.declining ?? 0, testid: "kpi-decl" },
              { label: "Active Alerts",    v: dash.active_alerts ?? 0, testid: "kpi-alerts" },
            ].map((k) => (
              <div key={k.label} data-testid={k.testid}
                    className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  {k.label}
                </div>
                <div className="text-2xl font-bold tabular-nums mt-1">{k.v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-3 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="market-search-input"
                  placeholder="Search zip, city, market…"
                  className="w-full pl-9 pr-3 py-2 rounded-md text-sm border border-slate-200 bg-white"/>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 size={20} className="animate-spin text-slate-400"/>
          </div>
        ) : zips.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center"
                data-testid="market-empty">
            <Map className="mx-auto text-slate-300 mb-3" size={40}/>
            <div className="text-sm text-slate-500">No markets tracked. Click <b>Track ZIP</b> to start.</div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                data-testid="market-zips-table">
            {zips.map((m) => (
              <ZipRow key={m.zip} zip={m} onClick={() => setSelected(m.zip)}/>
            ))}
          </div>
        )}
      </div>

      {showNew && <TrackZipModal onClose={() => setShowNew(false)}
                                     onCreated={() => { setShowNew(false); load(); }}/>}
      {selected && <ZipDrawer zip={selected}
                                  onClose={() => { setSelected(null); load(); }}/>}
    </div>
  );
}


function ZipRow({ zip, onClick }) {
  return (
    <button onClick={onClick}
              data-testid={`zip-row-${zip.zip}`}
              className="w-full flex items-center gap-3 px-4 py-3 border-t border-slate-100
                          hover:bg-sky-50/40 text-left">
      <Building2 size={16} className="text-slate-400"/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{zip.zip}</div>
        <div className="text-xs text-slate-500 truncate">
          {zip.city}{zip.state ? `, ${zip.state}` : ""}
        </div>
      </div>
      <div className="text-xs text-slate-600 hidden md:block">
        Price {fmt$(zip.median_price)} · Rent {fmt$(zip.median_rent)}
      </div>
      <ChevronRight size={14} className="text-slate-300"/>
    </button>
  );
}


function TrackZipModal({ onClose, onCreated }) {
  const [f, setF] = useState({ zip: "", city: "", state: "",
    median_price: "", median_rent: "", days_on_market: "",
    active_listings: "", offers_per_listing: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.zip.trim()) return toast.error("ZIP required");
    setSaving(true);
    try {
      const body = { ...f };
      ["median_price", "median_rent", "days_on_market",
        "active_listings", "offers_per_listing"].forEach((k) => {
        body[k] = f[k] ? parseFloat(f[k]) : undefined;
      });
      await api.post("/v3/market/zips", body);
      toast.success("ZIP tracked");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="market-create-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Track ZIP</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid gap-3 grid-cols-2">
          {[["zip", "ZIP *", "44113", true],
            ["city", "City"],
            ["state", "State"],
            ["median_price", "Median Price", "150000", false, "number"],
            ["median_rent", "Median Rent", "1500", false, "number"],
            ["days_on_market", "DOM (days)", "30", false, "number"],
            ["active_listings", "Active Listings", "50", false, "number"],
            ["offers_per_listing", "Offers/Listing", "2", false, "number"]].map(([k, l, ph, wide, type]) => (
            <label key={k} className={`text-xs ${wide ? "col-span-2" : ""}`}>
              {l}
              <input type={type || "text"} placeholder={ph}
                       value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                       data-testid={`zip-field-${k}`}
                       className="mt-1 w-full px-3 py-2 rounded-md text-sm border border-slate-200"/>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={saving}
                    data-testid="zip-submit-btn"
                    className="text-sm px-4 py-2 rounded-md bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 flex items-center gap-1">
            {saving && <Loader2 size={13} className="animate-spin"/>} Track
          </button>
        </div>
      </div>
    </div>
  );
}


function ZipDrawer({ zip, onClose }) {
  const [prof, setProf] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "trends",   label: "Trends" },
    { id: "fit",      label: "Strategy Fit" },
    { id: "forecast", label: "Forecast" },
    { id: "advisor",  label: "AI Advisor" },
  ];
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v3/market/${zip}`);
        setProf(data);
      } catch { toast.error("Load failed"); }
    })();
  }, [zip]);

  const generate = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/v3/market/advisor",
        { mode: "market_summary", zip });
      setAdvisor(data);
      if (data.cached) toast("Cached advisor");
      else toast.success("Advisor generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setAiLoading(false); }
  };

  if (!prof) return null;
  const t = TIER[prof.health.tier] || TIER.softening;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end"
          onClick={onClose} data-testid="zip-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 p-5 z-10">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-sky-600">
                Market · ZIP
              </div>
              <div className="text-xl font-bold" data-testid="drawer-zip">{prof.zip}</div>
              <div className="text-xs text-slate-500">
                {prof.metrics?.city}{prof.metrics?.state ? `, ${prof.metrics.state}` : ""}
              </div>
            </div>
            <button onClick={onClose} data-testid="drawer-close"><X size={20}/></button>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <div>
              <div className="text-[9px] uppercase text-slate-400 font-semibold">Health</div>
              <div className="text-3xl font-bold tabular-nums" style={{ color: t.text }}
                    data-testid="drawer-health-score">
                {prof.health.score.toFixed(0)}
              </div>
            </div>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ring-1"
                    style={{ color: t.text, background: t.bg, borderColor: t.border }}
                    data-testid="drawer-tier">
              {prof.health.tier}
            </span>
            <div className="ml-auto text-xs text-slate-500">
              Recommended: <b>{prof.strategy_fit.recommended}</b> ({prof.strategy_fit.recommended_score.toFixed(0)})
            </div>
          </div>
          <div className="flex gap-1 mt-4" data-testid="drawer-tabs">
            {TABS.map((tt) => (
              <button key={tt.id} onClick={() => setTab(tt.id)}
                        data-testid={`drawer-tab-${tt.id}`}
                        className={`text-xs px-3 py-1.5 rounded-md ${tab === tt.id
                          ? "bg-sky-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {tt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-5 space-y-3">
          {tab === "overview" && (
            <div className="grid grid-cols-2 gap-3 text-xs" data-testid="drawer-overview">
              {(prof.health.factors || []).map((f) => (
                <div key={f.key} data-testid={`factor-${f.key}`}
                      className="rounded-lg bg-slate-50/60 p-2">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">{f.label}</div>
                  <div className="text-lg font-bold tabular-nums">{f.score.toFixed(0)}</div>
                  <div className="text-[10px] text-slate-400">w {f.weight}% · contr {f.contribution.toFixed(1)}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "trends" && (
            <div className="text-sm space-y-2" data-testid="drawer-trends">
              {Object.entries(prof.trends).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                  <span className={`font-mono px-2 py-0.5 rounded text-[10px] ${
                    v === "improving" ? "bg-emerald-50 text-emerald-700" :
                    v === "declining" ? "bg-red-50 text-red-700" :
                    "bg-slate-50 text-slate-600"}`}>{v}</span>
                </div>
              ))}
            </div>
          )}
          {tab === "fit" && (
            <div className="space-y-2" data-testid="drawer-fit">
              {prof.strategy_fit.matrix.map((r) => (
                <div key={r.strategy} data-testid={`fit-${r.strategy}`}
                      className="flex items-center gap-2">
                  <div className="w-24 text-xs text-slate-700 capitalize">
                    {r.strategy.replace(/_/g, " ")}
                  </div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-sky-400 to-cyan-500"
                          style={{ width: `${r.score}%` }}/>
                  </div>
                  <div className="w-10 text-sm font-bold tabular-nums text-right">{r.score.toFixed(0)}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "forecast" && (
            <div className="space-y-2 text-xs" data-testid="drawer-forecast">
              {Object.entries(prof.forecast.projections || {}).map(([k, p]) =>
                p ? (
                  <div key={k} className="flex justify-between">
                    <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                    <span className="tabular-nums">
                      {p.current} → <b>{p.projected}</b> ({p.low} – {p.high})
                    </span>
                  </div>
                ) : (
                  <div key={k} className="text-slate-400 italic">
                    {k}: insufficient history
                  </div>
                )
              )}
            </div>
          )}
          {tab === "advisor" && (
            <div className="space-y-3" data-testid="drawer-advisor">
              <button onClick={generate} disabled={aiLoading}
                        data-testid="advisor-generate-btn"
                        className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                                    from-violet-500 to-fuchsia-500 text-white
                                    flex items-center gap-1 disabled:opacity-50">
                {aiLoading ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
                {advisor ? "Refresh" : "Generate"}
              </button>
              {advisor && (
                <div className="space-y-2">
                  <div className="text-sm font-medium" data-testid="advisor-headline">
                    {advisor.headline}
                  </div>
                  {["strengths", "opportunities", "recommended_actions"].map((k) => (
                    <div key={k} data-testid={`advisor-section-${k}`}>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">{k}</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        {(advisor[k] || []).map((it, i) => (
                          <li key={i}>• {typeof it === "string" ? it : it.text}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
